/**
 * @metadata CountryEnforcer
 * Componente global que centraliza la validación de país en páginas Edit y Show.
 * - En Edit: redirige a Show si el país del registro no coincide con el seleccionado.
 * - En Show: provee un alert visual y un flag para deshabilitar la edición.
 * Se monta una sola vez en el layout (AppRoutes) y usa el cache de React Query,
 * por lo que no genera requests HTTP adicionales.
 */
import { useGo, useMeta, useOne, useResourceParams } from '@refinedev/core';
import { Alert, message } from 'antd';
import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import SpinLoading from '@/components/general/SpinLoading';
import { useCountryContext } from '@/contexts/CountryContext';
import { RESOURCES } from '@/Resources';

interface CountryEnforcerContextType {
  /** Indica si el país del registro no coincide con el seleccionado (para deshabilitar edición en Show) */
  blockEditForCountry: boolean;
  /** Alerta visual para mostrar cuando hay mismatch de país en Show */
  alert: ReactNode;
}

const CountryEnforcerContext = createContext<CountryEnforcerContextType>({
  blockEditForCountry: false,
  alert: null,
});

export const useCountryEnforcer = () => useContext(CountryEnforcerContext);

interface CountryEnforcerProps {
  children: ReactNode;
}

const CountryEnforcer = ({ children }: CountryEnforcerProps) => {
  const { t } = useTranslation();
  const { action, resource, id } = useResourceParams();
  const { isBrasilSelected } = useCountryContext();
  const go = useGo();
  const getMeta = useMeta();
  // Solo fetchea cuando estamos en edit o show con un id válido.
  // useOne aprovecha el cache de React Query, así que no genera requests duplicados.
  const isEditOrShow = action === 'edit' || action === 'show';
  // CaseResolution no tiene endpoint propio; su datos vienen del recurso tickets.
  const fetchResource =
    resource?.name === RESOURCES.CASE_RESOLUTION ? RESOURCES.TICKETS : (resource?.name ?? '');
  const { result, query } = useOne({
    resource: fetchResource,
    id: id ?? '',
    // Replica el mismo meta que usa useShow() de la página para que la queryKey
    // coincida exactamente y React Query dedupee en una sola query real.
    meta: getMeta({ resource }),
    queryOptions: {
      enabled: isEditOrShow && !!resource?.name && !!id,
    },
  });
  const record = result as { is_br?: boolean; id?: string | number } | undefined;
  // Si ya hay datos en cache pero se está revalidando, esos datos pueden
  // corresponder a un registro de otro país que reutiliza el mismo id numérico
  // (el país no forma parte de la query key). No se puede confiar en `record`
  // hasta que la revalidación termine.
  const isValidatingCountry = isEditOrShow && query.isFetching && record !== undefined;
  const countryMismatch = useMemo(() => {
    if (isValidatingCountry || record?.is_br === undefined) return false;

    return (record.is_br && !isBrasilSelected) || (!record.is_br && isBrasilSelected);
  }, [record, isBrasilSelected, isValidatingCountry]);

  // Edit: redirige si el país no coincide
  useEffect(() => {
    if (action !== 'edit' || !record || !countryMismatch) return;

    message.error(t('Selecciona el país correcto para editar este recurso'));
    go({
      // Prioriza el id de la URL: el del record cacheado puede quedar desactualizado
      // si la cache compartida todavía no giró al recurso realmente visitado.
      to: { resource: resource?.name || '', action: 'show', id: (id ?? record.id)! },
      type: 'replace',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryMismatch, record, action]);

  // CaseResolution: redirige al listado si el país no coincide (los endpoints de listado filtran por país).
  useEffect(() => {
    if (resource?.name !== RESOURCES.CASE_RESOLUTION || !record || !countryMismatch) return;

    message.error(t('Selecciona el país correcto para ver este recurso'));
    go({
      to: { resource: RESOURCES.CASE_RESOLUTION, action: 'list' },
      type: 'replace',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryMismatch, record, resource?.name]);

  // Show: calcula el alert
  const alert = useMemo(() => {
    if (action !== 'show' || !countryMismatch) return null;

    return (
      <Alert
        style={{ margin: '16px 0' }}
        type="warning"
        message={
          isBrasilSelected
            ? t(
                'El recurso que se esta visualizando corresponde a argentina, cambia el país para poder modificarlo',
              )
            : t(
                'El recurso que se esta visualizando corresponde a brasil, cambia el país para poder modificarlo',
              )
        }
        showIcon
      />
    );
  }, [action, countryMismatch, isBrasilSelected, t]);
  const contextValue = useMemo(
    () => ({
      blockEditForCountry: action === 'show' && countryMismatch,
      alert,
    }),
    [action, countryMismatch, alert],
  );

  return (
    <CountryEnforcerContext.Provider value={contextValue}>
      {isValidatingCountry && <SpinLoading />}
      {/*
        `children` se mantiene siempre montado (solo se oculta con CSS) mientras
        se revalida un registro que puede ser de otro país cacheado bajo el mismo
        id numérico. Desmontarlo aquí quitaría su propio useShow()/useForm() como
        observer de la query compartida; al remontarlo, React Query refetchea de
        nuevo (staleTime 0 por defecto), lo que reactivaba isValidatingCountry y
        provocaba un loop infinito de desmontaje/fetch. `display: contents` hace
        que el wrapper no afecte el layout mientras se muestra normalmente.
      */}
      <div style={{ display: isValidatingCountry ? 'none' : 'contents' }}>{children}</div>
    </CountryEnforcerContext.Provider>
  );
};

export default CountryEnforcer;
